export interface ObjectReference {
  id: string;
  name: string;
}

export interface HierarchyReference {
  organization: ObjectReference;
  region: ObjectReference;
  building: ObjectReference;
  controlSystem: ObjectReference;
}

export interface ApiReference {
  apiConnectionId: string;
  apiSiteId: string;
}

export interface AwsInstance {
  instanceId: string;
  name: string;
  instanceType: string;
  state: string;
  platform: string;
  region: string;
  availabilityZone: string;
  privateIp: string;
  publicIp: string;
  vpcId: string;
  subnetId: string;
  launchTime: string;
  tags: Record<string, string>;
}

export interface AwsSalesOrderLine {
  salesOrderId: string;
  salesOrderNumber: string;
  lineNumber: number;
  service: string;
  status: string;
}

export interface AwsInstanceItem {
  instance: AwsInstance;
  salesOrders: AwsSalesOrderLine[];
  // Absent on records synced before device assignment existed.
  device?: ObjectReference;
}

export interface AwsInstanceRecord {
  id: string;
  name: string;
  apiReference: ApiReference;
  hierarchyReference: HierarchyReference;
  item: AwsInstanceItem;
  status: string;
  createdAt: string;
  lastSyncTime: string;
}

export type AwsInstanceRecords = AwsInstanceRecord[];

export interface AwsCostAmount {
  amount: string;
  unit: string;
}

export interface AwsCostPeriod {
  start: string;
  end: string;
}

export interface AwsInstanceCostItem {
  instanceId: string;
  period: AwsCostPeriod;
  amount: AwsCostAmount;
}

export interface AwsInstanceCostRecord {
  id: string;
  name: string;
  item: AwsInstanceCostItem;
  status: string;
  createdAt: string;
  lastSyncTime: string;
}

export type AwsInstanceCostRecords = AwsInstanceCostRecord[];

export interface AwsSummary {
  totalInstances: number;
  currentMonthCost: string;
  previousMonthCost: string;
  currency: string;
}

export interface AwsMonthlyCost {
  month: string;
  total: string;
  currency: string;
}

export type AwsMonthlyCosts = AwsMonthlyCost[];

export interface AwsInstanceCostSummary {
  instance: ObjectReference;
  monthlyCosts: Record<string, string>;
}

export interface AwsBuildingCostSummary {
  building: ObjectReference;
  monthlyCosts: Record<string, string>;
  instances: AwsInstanceCostSummary[];
}

export interface AwsOrganizationCostSummary {
  organization: ObjectReference;
  monthlyCosts: Record<string, string>;
  buildings: AwsBuildingCostSummary[];
}

export interface AwsHierarchyCostReport {
  months: string[];
  currency: string;
  organizations: AwsOrganizationCostSummary[];
}

export interface AwsStatusCostSummary {
  status: string;
  monthlyCosts: Record<string, string>;
}

export interface AwsStatusCostReport {
  months: string[];
  currency: string;
  statuses: AwsStatusCostSummary[];
}

export type AwsInstanceAction =
  'hierarchy' | 'salesOrder' | 'device' | 'status' | 'delete';

// "Discovered" is the machine-set default applied on every sync (see
// device-sync-service's awsSyncService.go). The rest are set manually by a
// user to classify what a running instance is actually for — either an
// internal category (Development, Overhead), a lifecycle state
// (Decommissioning), or which delivered service it backs (Asset Inventory,
// Remote Access, Optimize) — which keeps "Unassigned Running Instances"
// meaningful instead of flagging things forever that a human already
// reviewed. Alphabetical order.
export const AWS_INSTANCE_STATUS_OPTIONS = [
  'Asset Inventory',
  'Decommissioning',
  'Development',
  'Discovered',
  'Optimize',
  'Overhead',
  'Remote Access',
] as const;

export type AwsInstanceStatus = (typeof AWS_INSTANCE_STATUS_OPTIONS)[number];
